import { Chip, Stack, Typography } from '@mui/material'

import type { Filter, FilterType, SelectedFilters } from '../FilterableTable.types'

type Props<Data extends Record<string, unknown>> = {
  selectedFilters?: SelectedFilters
  onRemoveFilterValue: (paramName: string, filter: FilterType) => void
  filters?: Filter<Data>[]
}

const camelCaseToCapitalizedSpace = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (str) => str.toUpperCase())
}

const getFilterLabel =
  <Data extends Record<string, unknown>>(filters: Filter<Data>[]) =>
  (paramName: string): string => {
    const filter = filters.find((f) => f.paramName === paramName)
    return camelCaseToCapitalizedSpace(filter && 'label' in filter ? filter.label : paramName)
  }

export const SelectedFiltersChips = <Data extends Record<string, unknown>>({
  selectedFilters,
  onRemoveFilterValue,
  filters
}: Props<Data>) => {
  if (!selectedFilters) return null

  const getFilterLabelForCurrentFilters = getFilterLabel(filters || [])

  return (
    <Stack direction="row" gap={1}>
      {Object.entries(selectedFilters).map(([paramName, filterValues]) => (
        <Stack key={paramName} direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">{getFilterLabelForCurrentFilters(paramName)}:</Typography>
          {filterValues?.map(({ value, label }) => (
            <Chip
              key={`${paramName}-${label ?? value}`}
              label={label ?? value}
              size="small"
              onDelete={() => onRemoveFilterValue(paramName, { value, label })}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  )
}
